import { ReactComponent as RefineBeforePublishGraphic } from "@/components/ui/icons/refine-before-publish.svg";
import { WorkflowUpdateDialog } from "@faro-lotv/data-management";
import { FaroButton } from "@faro-lotv/flat-ui";
import { RegistrationRevision } from "@faro-lotv/service-wires";
import { DialogProps } from "@mui/material";
import { JSX } from "react";
import {
  useRefineRegistration,
  useSaveUserEdits,
} from "../../inspect-and-publish/refine-and-publish-utils";

type RefineBeforePublishDialogProps = Pick<DialogProps, "open"> & {
  /** The current revision to refine. */
  revision: RegistrationRevision;
  /** Called when the user dismisses the dialog without making a choice. */
  onCancel(): void;
  /** Called when the user chooses to publish without refinement. */
  onContinueWithoutRefinement(): void;
};

/**
 * @returns A dialog asking the user whether they want to refine the registration before publishing.
 *   This is shown when the registration could likely benefit from a refinement run.
 */
export function RefineBeforePublishDialog({
  revision,
  onCancel,
  onContinueWithoutRefinement,
  ...dialogProps
}: RefineBeforePublishDialogProps): JSX.Element {
  const { saveUserEdits } = useSaveUserEdits();
  const { isRefinementRunning, refine } = useRefineRegistration(
    revision,
    saveUserEdits,
  );

  return (
    <WorkflowUpdateDialog
      variant="action"
      title="Refine before publishing?"
      description={
        <>
          The registration can be automatically refined which may improve
          overall quality. Choose <b>Refine</b> to start, or{" "}
          <b>Continue without refinement</b> to publish the current result right
          away.
        </>
      }
      image={RefineBeforePublishGraphic}
      actions={
        <>
          <FaroButton
            variant="ghost"
            onClick={onCancel}
            disabled={isRefinementRunning}
          >
            Cancel
          </FaroButton>
          <FaroButton
            variant="secondary"
            onClick={onContinueWithoutRefinement}
            disabled={isRefinementRunning}
          >
            Continue without refinement
          </FaroButton>
          <FaroButton
            variant="primary"
            onClick={refine}
            isLoading={isRefinementRunning}
          >
            Refine
          </FaroButton>
        </>
      }
      {...dialogProps}
    />
  );
}
